import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

import { supabase } from '../services/supabaseClient';
import { useSubscription } from '../hooks/useSubscription';

const STRIPE_MODE   = process.env.REACT_APP_STRIPE_MODE ?? 'test';
const PRICE_MONTHLY = STRIPE_MODE === 'prod'
  ? (process.env.REACT_APP_STRIPE_PRICE_ID_MONTHLY_PROD ?? '')
  : (process.env.REACT_APP_STRIPE_PRICE_ID_MONTHLY_TEST ?? '');
const PRICE_YEARLY  = STRIPE_MODE === 'prod'
  ? (process.env.REACT_APP_STRIPE_PRICE_ID_YEARLY_PROD ?? '')
  : (process.env.REACT_APP_STRIPE_PRICE_ID_YEARLY_TEST ?? '');

const PRO_FEATURES = [
  'Flashcards — all 1,100+ words',
  'Hard Terms — all terms',
  'Synonyms — unlimited sessions + review mode',
  'Fast Attack — full play, spaced repetition & sync',
  'Quant — error categories, image attachments, review & export',
  'AI problem solver — unlimited explanations (trial: 1/day)',
  'Geometry — coming soon!',
  'Cross-device sync & progress tracking',
  'New features and content always included — no extra cost',
];

const SURFACE = 'var(--color-surface)';
const BORDER  = 'var(--color-border)';
const PRIMARY = 'var(--color-primary)';
const MUTED   = 'var(--color-text-muted)';

const UpgradePage: React.FC = () => {
  const { plan, isTrial, trialDaysLeft } = useSubscription();
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('yearly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [couponInput, setCouponInput] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponOpen, setCouponOpen] = useState(false);
  const priceId = billing === 'monthly' ? PRICE_MONTHLY : PRICE_YEARLY;
  const alreadyPro = plan === 'pro';

  const handleRedeemCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    setCouponError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/redeem-coupon`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ code: couponInput.trim() }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Failed to redeem coupon');
      window.location.href = '/?coupon_applied=1';
    } catch (err: any) {
      setCouponError(err.message);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleSubscribe = async () => {
if (!priceId) {
      setError('Stripe price not configured. Please contact support.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            priceId,
            successUrl: `${window.location.origin}/?subscribed=1`,
            cancelUrl: `${window.location.origin}/upgrade`,
          }),
        }
      );
      const { url, error: fnError } = await res.json();
      if (fnError) throw new Error(fnError);
      window.location.href = url;
    } catch (err: any) {
      setError(err.message ?? 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px 16px', maxWidth: 560, margin: '0 auto' }}>
      <Helmet>
        <title>Upgrade to Pro | Phrenos</title>
        <meta name="description" content="Upgrade to Phrenos Pro for unlimited access to all 1,100+ GRE vocab words, every study mode, spaced repetition, and cross-device sync." />
        <link rel="canonical" href="https://phrenos.app/upgrade" />
        <meta property="og:title" content="Upgrade to Pro | Phrenos" />
        <meta property="og:description" content="Unlock all 1,100+ GRE vocabulary words, every study mode, and cross-device sync with Phrenos Pro." />
        <meta property="og:url" content="https://phrenos.app/upgrade" />
        <meta name="robots" content="noindex" />
      </Helmet>

      <h2 style={{ fontSize: 24, fontWeight: 800, color: 'var(--color-text)', margin: '0 0 6px' }}>
        Phrenos Pro
      </h2>

      {isTrial && (
        <p style={{ fontSize: 14, color: PRIMARY, fontWeight: 600, margin: '0 0 20px' }}>
          Your free trial ends in {trialDaysLeft === 1 ? '1 day' : `${trialDaysLeft} days`}.
        </p>
      )}

      {alreadyPro ? (
        <p style={{ fontSize: 15, color: 'var(--color-text)', margin: '0 0 24px' }}>
          You're on Phrenos Pro. Manage your subscription from the profile menu.
        </p>
      ) : (
        <p style={{ fontSize: 15, color: MUTED, margin: '0 0 24px', lineHeight: 1.5 }}>
          Unlock every word, every mode, and cross-device sync.
        </p>
      )}

      {!alreadyPro && (
        <>
          {/* Billing toggle */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button
              onClick={() => setBilling('monthly')}
              style={{
                flex: 1, padding: '12px 8px', borderRadius: 12,
                fontWeight: 600, fontSize: 14, cursor: 'pointer',
                border: `1.5px solid ${billing === 'monthly' ? PRIMARY : BORDER}`,
                backgroundColor: billing === 'monthly' ? 'var(--color-success-light)' : SURFACE,
                color: billing === 'monthly' ? PRIMARY : MUTED,
              }}
            >
              $5 / month
            </button>
            <button
              onClick={() => setBilling('yearly')}
              style={{
                flex: 1, padding: '12px 8px', borderRadius: 12,
                fontWeight: 600, fontSize: 14, cursor: 'pointer',
                border: `2px solid ${billing === 'yearly' ? PRIMARY : BORDER}`,
                backgroundColor: billing === 'yearly' ? 'var(--color-success-light)' : SURFACE,
                color: billing === 'yearly' ? PRIMARY : MUTED,
                position: 'relative',
              }}
            >
              <span style={{
                position: 'absolute', top: -10, right: 10,
                backgroundColor: 'var(--color-amber)', color: '#fff',
                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
              }}>
                BEST VALUE
              </span>
              $39.99 / year
              <span style={{
                display: 'block', fontSize: 11, fontWeight: 500,
                color: billing === 'yearly' ? PRIMARY : MUTED, marginTop: 2,
              }}>
                save 33% — $3.33/mo
              </span>
            </button>
          </div>

          {/* Coupon code */}
          <div style={{ marginBottom: 16 }}>
            <button
              onClick={() => setCouponOpen(o => !o)}
              style={{
                background: 'none', border: 'none', padding: 0,
                fontSize: 12, color: MUTED, cursor: 'pointer',
                marginBottom: couponOpen ? 8 : 0,
                textDecoration: 'underline', textUnderlineOffset: 2,
              }}
            >
              Have a coupon code?
            </button>
            {couponOpen && <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <input
                value={couponInput}
                onChange={e => setCouponInput(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && handleRedeemCoupon()}
                placeholder="ENTER CODE"
                style={{
                  flex: 1, border: `1.5px solid ${BORDER}`, borderRadius: 10,
                  padding: '10px 12px', fontSize: 13, fontWeight: 600,
                  letterSpacing: '0.05em', backgroundColor: SURFACE,
                  outline: 'none', fontFamily: 'inherit',
                }}
              />
              <button
                onClick={handleRedeemCoupon}
                disabled={couponLoading || !couponInput.trim()}
                style={{
                  padding: '10px 16px', borderRadius: 10,
                  backgroundColor: PRIMARY, color: '#fff',
                  border: 'none', fontSize: 13, fontWeight: 700,
                  cursor: couponLoading || !couponInput.trim() ? 'not-allowed' : 'pointer',
                  opacity: couponLoading || !couponInput.trim() ? 0.6 : 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {couponLoading ? '…' : 'Apply'}
              </button>
            </div>}
            {couponOpen && couponError && (
              <p style={{ fontSize: 12, color: 'var(--color-danger)', marginTop: 6 }}>{couponError}</p>
            )}
          </div>

          {/* Subscribe button */}
          {error && (
            <p style={{ fontSize: 13, color: 'var(--color-danger)', marginBottom: 12 }}>{error}</p>
          )}
          <button
            onClick={handleSubscribe}
            disabled={loading}
            style={{
              width: '100%', padding: '14px 0',
              backgroundColor: PRIMARY, color: '#fff',
              border: 'none', borderRadius: 14,
              fontSize: 16, fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              marginBottom: 8,
            }}
          >
            {loading
              ? 'Redirecting to checkout…'
              : billing === 'monthly'
                ? 'Subscribe — $5/month'
                : 'Subscribe — $39.99/year'}
          </button>
          <p style={{ fontSize: 12, color: MUTED, textAlign: 'center', marginBottom: 24 }}>
            Cancel anytime. Secure checkout by Stripe.
          </p>

        </>
      )}

      {/* Pro features list */}
      <div style={{
        backgroundColor: SURFACE, border: `1px solid ${BORDER}`,
        borderRadius: 16, padding: '16px 14px',
      }}>
        <p style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: MUTED, margin: '0 0 12px' }}>
          Everything in Pro
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px' }}>
          {PRO_FEATURES.map(f => (
            <div key={f} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
              <span style={{ color: PRIMARY, flexShrink: 0, fontWeight: 700, fontSize: 12, marginTop: 2 }}>✓</span>
              <span style={{ fontSize: 12, color: 'var(--color-text)', lineHeight: 1.4 }}>{f}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UpgradePage;
